import { useGetOneFlow, useListFlowTypes } from "#hooks/adapters/useFlows";
import {
  AssetCreateMode,
  type AssetCreateModeType,
} from "#src/batteries-included-components/Buttons/CreateAssetDropdownMenu/CreateAssetDropdownMenu";
import { EquipmentDropdownInput } from "#src/batteries-included-components/Dropdowns/EquipmentDropdownInput";
import { FacilityDropdownInput } from "#src/batteries-included-components/Dropdowns/FacilityDropdownInput";
import { type AssetBuilderFiltersType } from "#src/batteries-included-components/FilterAreas/AssetBuilderFilterAreas/AssetBuilderFilterAreas";
import { AssetTemplateRadioInput } from "#src/batteries-included-components/Forms/Fields/AssetTemplateRadioInput/AssetTemplateRadioInput";
import { useListTemplatedConfigurations } from "#src/components/hooks/adapters/useTemplatedConfigurations";
import useLocalization from "#src/hooks/useLocalization";
import {
  useMemoizedFormState,
  useMultiStepFormContext,
} from "#src/hooks/useMultiStepForm";
import { useStickyState } from "#src/hooks/useStickyState";
import {
  DropdownInput,
  Form,
  Panel,
  TextInput,
  useForm,
  type StorageKeys,
} from "@validereinc/common-components";
import {
  AssetType,
  Resources,
  TemplatedConfigurationCompanyStatus,
  TemplatedConfigurationSchema,
  type AssetTypeType,
  type FlowType,
  type FlowTypesType,
} from "@validereinc/domain";
import { toTitleCaseString } from "@validereinc/utilities";
import classNames from "classnames/bind";
import React, { useEffect } from "react";
import styles from "./FlowFormPanel.module.scss";

const cx = classNames.bind(styles);

const STEP_NUMBER = 1;

export const FlowOverviewFormStep = ({
  filterConfigStorageKey,
  flowId,
  shouldUseAssetBuilder,
  assetCreateMode,
}: {
  flowId: string;
  shouldUseAssetBuilder?: boolean;
  assetCreateMode?: AssetCreateModeType;
} & Pick<StorageKeys, "filterConfigStorageKey">) => {
  const { currentStep, updateStep } = useMultiStepFormContext();
  const { localize, isLoading: isMappingLoading } = useLocalization();

  const [filters] = useStickyState<AssetBuilderFiltersType>(
    {} as AssetBuilderFiltersType,
    filterConfigStorageKey
  );
  const { displayName, sortDirection } = filters;

  const { data: flow, isLoading: isFlowLoading } = useGetOneFlow(
    {
      id: flowId,
    },
    {
      enabled: !!flowId,
    }
  );
  const flowTypes = useListFlowTypes().data?.data ?? [];

  const form = useForm<
    {
      asset_type: AssetTypeType;
      template_name?: string;
    } & Pick<
      FlowType,
      "name" | "type" | "associated_facility_id" | "associated_equipment_id"
    >
  >({
    defaultValues: {
      asset_type: AssetType.FLOW,
      name: "",
      associated_facility_id: "",
      associated_equipment_id: "",
      template_name: "",
    },
  });

  const getFormState = useMemoizedFormState(form);

  const parentId = form?.watch("associated_facility_id");

  const formValues = form.watch();

  const isTemplateQueryEnabled = shouldUseAssetBuilder;

  const {
    data: availableTemplatesData,
    isLoading: isLoadingAvailableTemplates,
  } = useListTemplatedConfigurations(
    {
      filters: {
        primary_resource_type: Resources.FLOW,
        [`entity_subtypes.${Resources.FLOW}`]: formValues.type,
        display_name: displayName,
        // @ts-expect-error TODO: Typing on filter
        templated_configuration_company_status: {
          $exact: TemplatedConfigurationCompanyStatus.ACTIVE,
        },
      },
      sortDirection,
      sortBy: TemplatedConfigurationSchema.keyof().Enum.display_name,
    },
    { enabled: isTemplateQueryEnabled }
  );

  const availableTemplates = availableTemplatesData?.data ?? [];

  const availableTemplatesOptions = availableTemplates.map((template) => {
    return {
      title: template.display_name,
      description: template.description ?? "",
      value: template.name,
    };
  });

  const handleTypeChange = () => {
    form.setValue("template_name", "");
  };

  const handleTemplateNameChange = (templateName: string) => {
    const flowTypeIds =
      availableTemplates?.find((template) => template.name === templateName)
        ?.entity_subtypes[Resources.FLOW] ?? [];
    if (flowTypeIds?.includes(formValues.type)) {
      return;
    }

    const newType =
      availableTemplates?.find((template) => template.name === templateName)
        ?.entity_subtypes[Resources.FLOW]?.[0] ?? "";

    form.setValue("type", newType as FlowTypesType, {
      shouldValidate: true,
    });
  };

  useEffect(() => {
    updateStep(STEP_NUMBER, {
      getValues: () => form.getValues(),
      getFormState: () => ({
        isSubmitting: false,
        isValid:
          !!formValues.type &&
          (!shouldUseAssetBuilder
            ? !!formValues.name
            : !!formValues.template_name),
      }),
    });
  }, [
    flow,
    getFormState,
    formValues.name,
    formValues.type,
    formValues.template_name,
  ]);

  useEffect(() => {
    form.setValue("associated_equipment_id", "");
  }, [parentId]);

  const titleCasedLocalizedFacility = toTitleCaseString(
    localize(Resources.FACILITY),
    { intent: "ui" }
  );
  const titleCasedLocalizedEquipment = toTitleCaseString(
    localize(Resources.EQUIPMENT),
    { intent: "ui" }
  );
  const isLoadingFlowEnabled = currentStep === STEP_NUMBER && !!flowId;
  const isLoading = (isLoadingFlowEnabled && isFlowLoading) || isMappingLoading;

  return (
    <>
      {currentStep === STEP_NUMBER ? (
        <Panel
          className={cx("panel")}
          loaded={!isLoading}
        >
          <Form {...form}>
            <h4 className={cx("section-title")}>Overview</h4>
            {assetCreateMode === AssetCreateMode.QUICK_CREATE ? (
              <p className={cx("section-description")}>
                {`Quick Create will create only the single ${localize(AssetType.FLOW)} whose details are specified below.`}
              </p>
            ) : null}
            <div className={cx("basic-info-container")}>
              {shouldUseAssetBuilder ? (
                <DropdownInput
                  label="Asset Type"
                  name="asset_type"
                  options={[{ label: localize(AssetType.FLOW), value: "flow" }]}
                  labelKey="label"
                  valueKey="value"
                  isRequired
                  isDisabled
                  isFluid
                />
              ) : (
                <TextInput
                  label="Name"
                  name="name"
                  placeholder="Enter Name..."
                  isFluid
                  isRequired
                  isDisabled={isLoading}
                />
              )}

              <DropdownInput
                name="type"
                label={`${localize(Resources.FLOW)} Type`}
                options={flowTypes}
                placeholder={`Select ${localize(Resources.FLOW)} Type...`}
                isSortedAlphabetically
                labelKey="name"
                valueKey="id"
                onChange={handleTypeChange}
                isRequired
                isDisabled={isLoading}
              />

              {shouldUseAssetBuilder ? (
                <>
                  <AssetTemplateRadioInput
                    filterConfigStorageKey={filterConfigStorageKey}
                    assetType={AssetType.FACILITY}
                    isTemplateQueryDisabled={!isTemplateQueryEnabled}
                    availableTemplatesQueryIsLoading={
                      isLoadingAvailableTemplates
                    }
                    handleOnChange={(templateName: string) =>
                      handleTemplateNameChange(templateName)
                    }
                    availableTemplatesOptions={availableTemplatesOptions}
                    isOptionSelected={Boolean(formValues.template_name)}
                  />
                </>
              ) : (
                <>
                  <FacilityDropdownInput
                    name="associated_facility_id"
                    label={`Associated ${titleCasedLocalizedFacility}`}
                    placeholder={`Select associated ${titleCasedLocalizedFacility}...`}
                    isMulti={false}
                    isOptionalTextShown={true}
                    isDisabled={isLoading}
                  />
                  <EquipmentDropdownInput
                    name="associated_equipment_id"
                    label={`Associated ${titleCasedLocalizedEquipment}`}
                    placeholder={`Select associated ${titleCasedLocalizedEquipment}...`}
                    isMulti={false}
                    isOptionalTextShown={true}
                    refetch={parentId}
                    fetchFilters={parentId ? { "facility.id": parentId } : {}}
                    isDisabled={isLoading}
                  />
                </>
              )}
            </div>
          </Form>
        </Panel>
      ) : null}
    </>
  );
};
